import { atom } from '@reatom/framework'

import type { Ctx } from '@reatom/framework'

/**
 * How long a row counts as having just arrived, in milliseconds.
 *
 * The design gives the arrival wash two seconds. Rows re-render on a one-second
 * clock, so the mark is seen for one or two ticks and then goes.
 */
export const JUST_LANDED_MS = 2000

/**
 * How long an arrival is remembered at all.
 *
 * Longer than the window it is shown for, so a row that renders a little late
 * still learns it arrived, but short enough that the record stays a handful of
 * entries rather than growing for the life of the session.
 */
const REMEMBER_MS = 10_000

/**
 * When each pair entered a list.
 *
 * The socket knows a pool is new — its payload carries `append` — but until now
 * that was thrown away the moment the list was updated, so nothing downstream
 * could tell an arrival from a row that had been sitting there. This keeps it,
 * and only this: *age* is no substitute, because a pool is created seconds
 * before it reaches a feed, and while a column is paused arrivals queue up and
 * land together much later. Arrival is when the reader saw it, not when the
 * pool was born.
 */
export const pairArrivalsAtom = atom(
  new Map<string, number>(),
  'pairArrivalsAtom',
)

/**
 * Record that these pairs have just entered a list, and forget stale ones.
 *
 * Called from the single place that knows — the append that unshifts a pair
 * into a column — so a pair that was already on the list, or arrived twice in
 * one batch, is not marked again.
 */
export const markPairsArrived = (
  ctx: Ctx,
  addresses: ReadonlyArray<string>,
  now: number = Date.now(),
): void => {
  if (addresses.length === 0) {
    return
  }

  const next = new Map<string, number>()

  for (const [address, at] of ctx.get(pairArrivalsAtom)) {
    if (now - at < REMEMBER_MS) {
      next.set(address, at)
    }
  }

  for (const address of addresses) {
    next.set(address, now)
  }

  pairArrivalsAtom(ctx, next)
}

/** Whether a pair arrived recently enough to still be worth marking. */
export const justLanded = (
  arrivedAt: number | undefined,
  now: number,
): boolean => {
  if (arrivedAt === undefined) {
    return false
  }
  return now - arrivedAt < JUST_LANDED_MS
}
